// src/components/CapacityPage.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../AuthContext';
import { PHASES } from '../constants';

// ── Default scoring config ─────────────────────────────────
const DEFAULT_PLATFORM_SCORES = {
  'GAds_new_lp_garage':   { label: 'New Google Ads + LP + Garage Doors', points: 14 },
  'GAds_new_lp':          { label: 'New Google Ads + LP',                 points: 10 },
  'GAds_exist_lp':        { label: 'Existing Google Ads + LP',            points: 10 },
  'GAds_new_garage':      { label: 'New Google Ads (no LP) + Garage Doors', points: 12 },
  'GAds_new':             { label: 'New Google Ads (no LP)',               points: 8  },
  'GAds_exist':           { label: 'Existing Google Ads (no LP)',          points: 8  },
  'Meta_lp':              { label: 'Meta + LP',                            points: 10 },
  'Meta':                 { label: 'Meta (no LP)',                         points: 7  },
  'MSFT_new_lp':          { label: 'New Microsoft + LP',                   points: 8  },
  'MSFT_new':             { label: 'New Microsoft (no LP)',                 points: 6  },
  'MSFT_exist':           { label: 'Existing Microsoft',                   points: 6  },
  'LSA_new_garage':       { label: 'New LSA (Garage Doors)',               points: 12 },
  'LSA_new':              { label: 'New LSA',                              points: 8  },
  'LSA_exist':            { label: 'Existing LSA',                         points: 4  },
  'Yelp':                 { label: 'Yelp',                                 points: 4  },
  'Nextdoor':             { label: 'Nextdoor',                             points: 4  },
};

const DEFAULT_THRESHOLDS = { green: 40, yellow: 60, orange: 80, red: 100 };

const DEFAULT_MODIFIERS = {
  extraVerticalPts: 1.5,
  budget5k: 1,
  budget10k: 2,
  budget20k: 3,
  multiPlatformPts: 2,
};

const PHASE_WEIGHTS = {
  access:    { active: 0,    incoming: 0.8,  label: 'Incoming' },
  analysis:  { active: 0.8,  incoming: 0,    label: 'Active'   },
  building:  { active: 1.0,  incoming: 0,    label: 'Active'   },
  built:     { active: 0.4,  incoming: 0,    label: 'Active'   },
  tracking:  { active: 0.7,  incoming: 0,    label: 'Active'   },
  qa:        { active: 0.6,  incoming: 0,    label: 'Active'   },
  prelaunch: { active: 0.5,  incoming: 0,    label: 'Active'   },
  postlaunch:{ active: 0,    incoming: 0,    label: 'Complete' },
};

// ── Scoring engine ─────────────────────────────────────────
function scorePlatform(plat, client, scores) {
  const isGD = Array.isArray(client.verticals)
    ? client.verticals.includes('Garage Doors')
    : client.vertical === 'Garage Doors';
  const hasLP = client.adsDestination === 'Landing Pages';
  const aType = plat.accountType || 'new';
  const name = plat.name;
  const budget = parseFloat(plat.budget) || 0;

  let key = '';
  if (name === 'Google Ads') {
    if (aType === 'new' && hasLP && isGD) key = 'GAds_new_lp_garage';
    else if (aType === 'new' && hasLP)     key = 'GAds_new_lp';
    else if (aType === 'existing' && hasLP) key = 'GAds_exist_lp';
    else if (aType === 'new' && isGD)      key = 'GAds_new_garage';
    else if (aType === 'new')              key = 'GAds_new';
    else                                   key = 'GAds_exist';
  } else if (name === 'Microsoft') {
    if (aType === 'new' && hasLP) key = 'MSFT_new_lp';
    else if (aType === 'new')     key = 'MSFT_new';
    else                          key = 'MSFT_exist';
  } else if (name === 'Meta') {
    key = hasLP ? 'Meta_lp' : 'Meta';
  } else if (name === 'LSA') {
    if (aType === 'new' && isGD) key = 'LSA_new_garage';
    else if (aType === 'new')    key = 'LSA_new';
    else                         key = 'LSA_exist';
  } else if (name === 'Yelp') {
    key = 'Yelp';
  } else if (name === 'Nextdoor') {
    key = 'Nextdoor';
  }

  const base = scores[key]?.points ?? 0;

  // Budget modifier per platform
  let budgetMod = 0;
  if (budget >= 20000) budgetMod = 3;
  else if (budget >= 10000) budgetMod = 2;
  else if (budget >= 5000) budgetMod = 1;

  return { key, base, budgetMod, total: base + budgetMod };
}

function scoreClient(client, scores, modifiers) {
  if (client.onHold || client.phase === 'postlaunch' || client.phase === 'cancelled') {
    return { raw: 0, active: 0, incoming: 0, breakdown: [] };
  }

  const platforms = client.platforms || [];
  const verticals = Array.isArray(client.verticals) ? client.verticals : (client.vertical ? [client.vertical] : []);
  const extraVerticals = Math.max(0, verticals.length - 1);
  const verticalMod = extraVerticals * (modifiers.extraVerticalPts ?? 1.5);
  const multiMod = platforms.length > 1 ? (modifiers.multiPlatformPts ?? 2) : 0;

  const breakdown = platforms.map(p => scorePlatform(p, client, scores));
  const platformTotal = breakdown.reduce((s, b) => s + b.total, 0);
  const raw = platformTotal + verticalMod + multiMod;

  const phaseW = PHASE_WEIGHTS[client.phase] || { active: 0, incoming: 0 };
  const active = Math.round(raw * phaseW.active * 10) / 10;
  const incoming = Math.round(raw * phaseW.incoming * 10) / 10;

  return { raw, active, incoming, breakdown, verticalMod, multiMod, phaseW };
}

// ── Capacity color ─────────────────────────────────────────
function capacityColor(pts, thresholds) {
  if (pts >= thresholds.red)    return { bg: '#FCEBEB', text: '#A32D2D', bar: '#E24B4A', label: 'Over Capacity' };
  if (pts >= thresholds.orange) return { bg: '#FAEEDA', text: '#854F0B', bar: '#EF9F27', label: 'High' };
  if (pts >= thresholds.yellow) return { bg: '#FFF8E1', text: '#7A5C00', bar: '#F5C842', label: 'Moderate' };
  return { bg: '#EAF3DE', text: '#27500A', bar: '#1D9E75', label: 'Available' };
}

// ── Main component ─────────────────────────────────────────
export default function CapacityPage({ allUsers }) {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'supervisor';

  const [allClients, setAllClients] = useState([]);
  const [scores, setScores] = useState(DEFAULT_PLATFORM_SCORES);
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [modifiers, setModifiers] = useState(DEFAULT_MODIFIERS);
  const [showSettings, setShowSettings] = useState(false);
  const [editScores, setEditScores] = useState({});
  const [editThresh, setEditThresh] = useState({});
  const [editMod, setEditMod] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  // Load clients
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'clients'), snap => {
      setAllClients(snap.docs.map(d => ({ ...d.data(), id: d.id })));
    });
    return unsub;
  }, []);

  // Load scoring config from Firestore
  useEffect(() => {
    getDoc(doc(db, 'config', 'capacity')).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.scores)     setScores({ ...DEFAULT_PLATFORM_SCORES, ...d.scores });
        if (d.thresholds) setThresholds({ ...DEFAULT_THRESHOLDS, ...d.thresholds });
        if (d.modifiers)  setModifiers({ ...DEFAULT_MODIFIERS, ...d.modifiers });
      }
    });
  }, []);

  async function saveSettings() {
    setSaving(true);
    const merged = {
      scores: Object.fromEntries(
        Object.entries(editScores).map(([k, v]) => [k, { ...scores[k], points: parseFloat(v) || scores[k].points }])
      ),
      thresholds: {
        green:  parseFloat(editThresh.green)  || thresholds.green,
        yellow: parseFloat(editThresh.yellow) || thresholds.yellow,
        orange: parseFloat(editThresh.orange) || thresholds.orange,
        red:    parseFloat(editThresh.red)    || thresholds.red,
      },
      modifiers: {
        extraVerticalPts: parseFloat(editMod.extraVerticalPts) || modifiers.extraVerticalPts,
        budget5k:  parseFloat(editMod.budget5k)  || modifiers.budget5k,
        budget10k: parseFloat(editMod.budget10k) || modifiers.budget10k,
        budget20k: parseFloat(editMod.budget20k) || modifiers.budget20k,
        multiPlatformPts: parseFloat(editMod.multiPlatformPts) || modifiers.multiPlatformPts,
      },
    };
    const newScores = { ...scores, ...merged.scores };
    setScores(newScores);
    setThresholds(merged.thresholds);
    setModifiers(merged.modifiers);
    await setDoc(doc(db, 'config', 'capacity'), { scores: newScores, thresholds: merged.thresholds, modifiers: merged.modifiers });
    setSaving(false);
    setShowSettings(false);
  }

  // Only users and admins get capacity cards — not viewer, pm, or supervisor
  const strategists = allUsers.filter(u => u.role === 'user' || u.role === 'admin');

  // Score every client
  const clientScores = {};
  allClients.forEach(c => {
    clientScores[c.id] = scoreClient(c, scores, modifiers);
  });

  // Per-user aggregates
  function userClients(uid) {
    return allClients.filter(c => c.ownerUid === uid && !c.onHold && c.phase !== 'postlaunch' && c.phase !== 'cancelled');
  }

  function userStats(uid) {
    const clients = userClients(uid);
    const active = clients.filter(c => c.phase !== 'access');
    const incoming = clients.filter(c => c.phase === 'access');
    const totalActive = active.reduce((s, c) => s + (clientScores[c.id]?.active || 0), 0);
    const totalIncoming = incoming.reduce((s, c) => s + (clientScores[c.id]?.incoming || 0), 0);
    const totalRaw = clients.reduce((s, c) => s + (clientScores[c.id]?.raw || 0), 0);
    return {
      totalActive: Math.round(totalActive * 10) / 10,
      totalIncoming: Math.round(totalIncoming * 10) / 10,
      totalRaw: Math.round(totalRaw * 10) / 10,
      activeCount: active.length,
      incomingCount: incoming.length,
      onHoldCount: allClients.filter(c => c.ownerUid === uid && c.onHold).length,
    };
  }

  const platShort = n => ({ 'Google Ads': 'GAds', Microsoft: 'MSFT', LSA: 'LSA', Meta: 'Meta', Yelp: 'Yelp', Nextdoor: 'Nextdoor' }[n] || n);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f5f5f3', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>📊 Team Capacity</div>
          <div style={{ fontSize: 11, color: '#888' }}>Workload analysis based on platform complexity, phase, and account factors</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Threshold legend */}
          {[
            { label: 'Available', color: '#1D9E75', bg: '#EAF3DE' },
            { label: 'Moderate', color: '#7A5C00', bg: '#FFF8E1' },
            { label: 'High', color: '#854F0B', bg: '#FAEEDA' },
            { label: 'Over Capacity', color: '#A32D2D', bg: '#FCEBEB' },
          ].map(l => (
            <span key={l.label} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 700, background: l.bg, color: l.color }}>{l.label}</span>
          ))}
          {isAdmin && (
            <button onClick={() => { setEditScores(Object.fromEntries(Object.entries(scores).map(([k,v]) => [k, v.points]))); setEditThresh({...thresholds}); setEditMod({...modifiers}); setShowSettings(true); }}
              style={{ fontSize: 12, padding: '5px 12px', background: '#f5f5f3', border: '1px solid rgba(0,0,0,0.18)', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' }}>
              ⚙ Scoring Settings
            </button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {/* Team Overview — side by side cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 24 }}>
          {strategists.map(u => {
            const stats = userStats(u.uid);
            const col = capacityColor(stats.totalActive, thresholds);
            const pct = Math.min(100, Math.round((stats.totalActive / thresholds.red) * 100));
            const initials = (u.displayName || u.email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            return (
              <div key={u.uid} style={{ background: '#fff', borderRadius: 12, border: `1px solid rgba(0,0,0,0.1)`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                {/* Top color bar */}
                <div style={{ height: 5, background: col.bar }} />
                <div style={{ padding: '14px 16px' }}>
                  {/* Name row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: col.bar, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{initials}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{u.displayName || u.email}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: col.text, background: col.bg, borderRadius: 10, padding: '1px 8px', display: 'inline-block' }}>{col.label}</div>
                    </div>
                  </div>

                  {/* Big numbers */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1, textAlign: 'center', background: col.bg, borderRadius: 8, padding: '8px 4px' }}>
                      <div style={{ fontSize: 26, fontWeight: 900, color: col.text, lineHeight: 1 }}>{stats.totalActive}</div>
                      <div style={{ fontSize: 10, color: col.text, fontWeight: 600, marginTop: 2 }}>Active Pts</div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', background: '#FAEEDA', borderRadius: 8, padding: '8px 4px' }}>
                      <div style={{ fontSize: 26, fontWeight: 900, color: '#854F0B', lineHeight: 1 }}>{stats.totalIncoming}</div>
                      <div style={{ fontSize: 10, color: '#854F0B', fontWeight: 600, marginTop: 2 }}>Incoming Pts</div>
                    </div>
                  </div>

                  {/* Capacity bar */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginBottom: 3 }}>
                      <span>Capacity</span>
                      <span>{pct}% of max ({thresholds.red} pts)</span>
                    </div>
                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                      {/* Threshold markers */}
                      {[thresholds.green, thresholds.yellow, thresholds.orange].map(t => (
                        <div key={t} style={{ position: 'absolute', left: `${Math.min(100,(t/thresholds.red)*100)}%`, top: 0, bottom: 0, width: 1, background: 'rgba(0,0,0,0.15)', zIndex: 1 }} />
                      ))}
                      <div style={{ height: '100%', background: col.bar, borderRadius: 4, width: `${pct}%`, transition: 'width 0.4s', position: 'relative', zIndex: 2 }} />
                    </div>
                  </div>

                  {/* Account counts */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Active', value: stats.activeCount, bg: '#E6F1FB', color: '#185FA5' },
                      { label: 'Incoming', value: stats.incomingCount, bg: '#FAEEDA', color: '#854F0B' },
                      { label: 'On Hold', value: stats.onHoldCount, bg: '#f5f5f3', color: '#888' },
                    ].map(s => (
                      <div key={s.label} style={{ background: s.bg, borderRadius: 6, padding: '3px 8px', display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: s.color }}>{s.value}</span>
                        <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>{s.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Expand toggle */}
                  <button onClick={() => setExpandedUser(expandedUser === u.uid ? null : u.uid)}
                    style={{ width: '100%', marginTop: 10, fontSize: 11, padding: '5px', background: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 7, cursor: 'pointer', color: '#888', fontFamily: 'inherit' }}>
                    {expandedUser === u.uid ? '▲ Hide breakdown' : '▼ Show account breakdown'}
                  </button>
                </div>

                {/* Expanded breakdown */}
                {expandedUser === u.uid && (
                  <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', background: '#fafafa' }}>
                    <AccountBreakdown
                      clients={allClients.filter(c => c.ownerUid === u.uid && c.phase !== 'postlaunch' && c.phase !== 'cancelled' && !c.onHold)}
                      clientScores={clientScores}
                      scores={scores}
                      thresholds={thresholds}
                      platShort={platShort}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Full team comparison table */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)', fontSize: 12, fontWeight: 700, color: '#555' }}>
            Team Comparison
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Strategist','Active Pts','Incoming Pts','Combined','Active Accounts','Incoming','On Hold','Capacity'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', background: '#f8f8f8', borderBottom: '1px solid rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...strategists].sort((a,b) => (userStats(b.uid).totalActive) - (userStats(a.uid).totalActive)).map(u => {
                const stats = userStats(u.uid);
                const col = capacityColor(stats.totalActive, thresholds);
                const pct = Math.min(100, Math.round((stats.totalActive / thresholds.red) * 100));
                return (
                  <tr key={u.uid} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{u.displayName || u.email}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontWeight: 800, fontSize: 14, color: col.text }}>{stats.totalActive}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontWeight: 700, color: '#854F0B' }}>{stats.totalIncoming}</span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontWeight: 700, color: '#555' }}>{Math.round((stats.totalActive + stats.totalIncoming) * 10) / 10}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{stats.activeCount}</td>
                    <td style={{ padding: '10px 14px', color: '#854F0B' }}>{stats.incomingCount}</td>
                    <td style={{ padding: '10px 14px', color: '#888' }}>{stats.onHoldCount}</td>
                    <td style={{ padding: '10px 14px', minWidth: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 7, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: col.bar, width: `${pct}%`, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: col.text, whiteSpace: 'nowrap' }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>

      {/* Settings Modal — Admin only */}
      {showSettings && isAdmin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 400, padding: 20, overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 640, margin: 'auto', border: '1px solid rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800, flex: 1 }}>⚙ Scoring Settings</div>
              <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#aaa' }}>✕</button>
            </div>
            <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>

              {/* Platform scores */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>Platform Base Points</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, marginBottom: 20 }}>
                {Object.entries(scores).map(([key, val]) => (
                  <React.Fragment key={key}>
                    <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', color: '#333' }}>{val.label}</div>
                    <input
                      type="number" min="0" max="30" step="0.5"
                      value={editScores[key] ?? val.points}
                      onChange={e => setEditScores(s => ({ ...s, [key]: e.target.value }))}
                      style={{ width: 60, fontSize: 13, padding: '4px 8px', border: '1px solid rgba(0,0,0,0.18)', borderRadius: 6, textAlign: 'center', fontWeight: 700, color: '#185FA5' }}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Modifiers */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>Complexity Modifiers</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6, marginBottom: 20 }}>
                {[
                  { key: 'extraVerticalPts', label: 'Extra pts per additional vertical' },
                  { key: 'budget5k',  label: 'Budget $5k–$10k add-on (per platform)' },
                  { key: 'budget10k', label: 'Budget $10k–$20k add-on (per platform)' },
                  { key: 'budget20k', label: 'Budget $20k+ add-on (per platform)' },
                  { key: 'multiPlatformPts', label: 'Multi-platform account add-on' },
                ].map(m => (
                  <React.Fragment key={m.key}>
                    <div style={{ fontSize: 12, color: '#333', display: 'flex', alignItems: 'center' }}>{m.label}</div>
                    <input
                      type="number" min="0" step="0.5"
                      value={editMod[m.key] ?? modifiers[m.key]}
                      onChange={e => setEditMod(s => ({ ...s, [m.key]: e.target.value }))}
                      style={{ width: 60, fontSize: 13, padding: '4px 8px', border: '1px solid rgba(0,0,0,0.18)', borderRadius: 6, textAlign: 'center', fontWeight: 700, color: '#185FA5' }}
                    />
                  </React.Fragment>
                ))}
              </div>

              {/* Thresholds */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', marginBottom: 10 }}>Capacity Thresholds (points)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6 }}>
                {[
                  { key: 'green',  label: '🟢 Available → Moderate threshold' },
                  { key: 'yellow', label: '🟡 Moderate → High threshold' },
                  { key: 'orange', label: '🟠 High → Over Capacity threshold' },
                  { key: 'red',    label: '🔴 Maximum / Full Capacity' },
                ].map(t => (
                  <React.Fragment key={t.key}>
                    <div style={{ fontSize: 12, color: '#333', display: 'flex', alignItems: 'center' }}>{t.label}</div>
                    <input
                      type="number" min="0"
                      value={editThresh[t.key] ?? thresholds[t.key]}
                      onChange={e => setEditThresh(s => ({ ...s, [t.key]: e.target.value }))}
                      style={{ width: 60, fontSize: 13, padding: '4px 8px', border: '1px solid rgba(0,0,0,0.18)', borderRadius: 6, textAlign: 'center', fontWeight: 700 }}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowSettings(false)} style={{ fontSize: 12, padding: '7px 14px', borderRadius: 7, background: '#f5f5f3', border: '1px solid rgba(0,0,0,0.18)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveSettings} disabled={saving} style={{ fontSize: 12, padding: '7px 18px', borderRadius: 7, background: saving ? '#aaa' : '#185FA5', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>
                {saving ? 'Saving…' : '💾 Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Account breakdown sub-component ───────────────────────
function AccountBreakdown({ clients, clientScores, scores, thresholds, platShort }) {
  const phaseLabel = { access: 'Access & Deliverables', analysis: 'Analysis', building: 'Building', built: 'Built (waiting)', tracking: 'Tracking', qa: 'Q/A', prelaunch: 'Prelaunch', postlaunch: 'Launched' };
  const phaseColor = { access: '#854F0B', analysis: '#185FA5', building: '#185FA5', built: '#3C3489', tracking: '#0F6E56', qa: '#27500A', prelaunch: '#0F6E56', postlaunch: '#5F5E5A' };

  const sorted = [...clients].sort((a, b) => {
    const ap = clientScores[a.id]?.active || 0;
    const bp = clientScores[b.id]?.active || 0;
    return bp - ap;
  });

  return (
    <div style={{ padding: '8px 0' }}>
      {sorted.map(c => {
        const cs = clientScores[c.id] || { raw: 0, active: 0, incoming: 0 };
        const isIncoming = c.phase === 'access';
        const isOnHold = c.onHold;
        const isLaunched = c.phase === 'postlaunch';
        const pts = isIncoming ? cs.incoming : cs.active;
        const pCol = isOnHold ? '#888' : isLaunched ? '#0F6E56' : isIncoming ? '#854F0B' : '#185FA5';
        const pBg = isOnHold ? '#f5f5f3' : isLaunched ? '#EAF3DE' : isIncoming ? '#FAEEDA' : '#E6F1FB';

        return (
          <div key={c.id} style={{ padding: '7px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.name}
                {isOnHold && <span style={{ marginLeft: 6, fontSize: 10, color: '#888' }}>• On Hold</span>}
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                {(c.platforms || []).map((p, i) => {
                  const sc = cs.breakdown?.[i];
                  return (
                    <span key={i} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#eee', color: '#555', fontWeight: 600 }}>
                      {platShort(p.name)} {sc ? `(${sc.base}${sc.budgetMod > 0 ? '+' + sc.budgetMod : ''})` : ''}
                    </span>
                  );
                })}
                {cs.verticalMod > 0 && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f0f0f0', color: '#666' }}>+{cs.verticalMod} verticals</span>}
                {cs.multiMod > 0 && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f0f0f0', color: '#666' }}>+{cs.multiMod} multi-plat</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: phaseColor[c.phase] || '#888', display: 'block' }}>{phaseLabel[c.phase] || c.phase}</span>
              <span style={{ fontSize: 14, fontWeight: 900, color: pCol, background: pBg, borderRadius: 5, padding: '1px 7px', display: 'inline-block', marginTop: 2 }}>
                {pts} {isIncoming ? 'inc' : 'pts'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
